import React, { Fragment, useRef, useState } from 'react';
import {
    Button,
    ButtonGroup,
    Tooltip,
    Popover,
    makeStyles,
    Typography,
    useTheme,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { X } from 'react-feather';
import { DateRangeCalendar } from './DateRangeCalendar';
import { IDateFilter } from './types';

const useStyles = makeStyles(() => ({
    popover: {
        width: 350,
    },
}));

/**
 * Represents a date filter component for the lead list.
 *
 * @param {IDateFilter<TField>} {
 *     field,
 *     setCurrentDateRange,
 *     value,
 *     onClear,
 *     handleDateRangeChange,
 * }
 */
export const DateFilter = <TField,>({
    field,
    setCurrentDateRange,
    value,
    onClear,
    handleDateRangeChange,
}: IDateFilter<TField>) => {
    const { t } = useTranslation();
    const [isOpen, setOpen] = useState(false);
    const classes = useStyles();
    const currentButtonRef = useRef<HTMLButtonElement>(null);
    const theme = useTheme();

    const noDateRangeLabel: string = t(`ModalFilters.noDateRange`);
    const dateRangeLabel: string = t(`ModalFilters.${field}`);
    const clearDateRangeLabel: string = t(`ModalFilters.clearDateRange`);

    return (
        <Fragment>
            <Popover
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                }}
                classes={{ paper: classes.popover }}
                anchorEl={currentButtonRef.current}
                onClose={() => setOpen(false)}
                open={isOpen}
            >
                <DateRangeCalendar
                    value={value || null}
                    onChange={handleDateRangeChange(field)}
                />
            </Popover>
            <Typography
                variant="h6"
                style={{ minWidth: 85 }}
                data-testid="date-filter-label"
            >
                {dateRangeLabel}
            </Typography>
            <ButtonGroup fullWidth style={{ height: 45 }}>
                <Button
                    style={{
                        textTransform: 'inherit',
                        width: '90%',
                        fontWeight: 400,
                        color: theme.palette.text.secondary,
                        fontSize: 16,
                        fontFamily: theme.typography.fontFamily,
                        justifyContent: 'flex-start',
                    }}
                    onClick={() => {
                        setCurrentDateRange(field);
                        setOpen(true);
                    }}
                    ref={currentButtonRef}
                    data-testid="date-filter-button"
                >
                    {value?.gte && value?.lt
                        ? `${dateRangeLabel}: ${' '}
                        ${moment(value.gte).format('DD MMMM YYYY')}
                        ${' - '}
                        ${moment(value.lt).format('DD MMMM YYYY')}`
                        : noDateRangeLabel}
                </Button>
                <Tooltip title={clearDateRangeLabel}>
                    <Button
                        onClick={onClear}
                        variant="outlined"
                        data-testid="date-filter-clear-button"
                        style={{
                            width: '10%',
                        }}
                    >
                        <X />
                    </Button>
                </Tooltip>
            </ButtonGroup>
        </Fragment>
    );
};
